import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import { ID, JobStatus, PayPeriodActions } from 'types/entity'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import useSessionStorage from 'core/hooks/utils/useSessionStorage'

interface PayPeriodActionProps {
  onSuccess?: VoidFunction
}

interface PayPeriodLocalStorage {
  id: ID
  action: PayPeriodActions | ''
  jobId: ID
}

const usePayPeriodAction = ({ onSuccess }: PayPeriodActionProps = {}) => {
  const [payPeriodData, setPayPeriodData] =
    useSessionStorage<PayPeriodLocalStorage>('pay_period_action_job', {
      id: '',
      action: '',
      jobId: ''
    })
  const queryClient = useQueryClient()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const jobID = settings.pollingJobs.payPeriodActionJobId || payPeriodData.jobId

  useQueryCallbacks({
    queryKey: ['pay-period-action-polling', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      if (response.status === 'finished' && !response.data?.errors) {
        notification.dismiss('payPeriodActionJobId')
        setPayPeriodData({
          ...payPeriodData,
          jobId: ''
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodActionJobId: ''
        })
        queryClient.invalidateQueries({
          queryKey: payPeriodData.id
            ? ['pay-periods', payPeriodData.id]
            : ['pay-periods']
        })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        notification.success({
          title: response.data.message as string,
          id: String(jobID)
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && response.data?.errors)
      ) {
        notification.dismiss('payPeriodActionJobId')
        notification.error({
          title: 'There were some errors updating your pay period',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: String(jobID)
        })
        setPayPeriodData({
          ...payPeriodData,
          jobId: ''
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodActionJobId: ''
        })
      }

      if (response.status === 'finished') {
        queryClient.invalidateQueries({
          queryKey: payPeriodData.id
            ? ['pay-periods', payPeriodData.id]
            : ['pay-periods']
        })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        setPayPeriodData({
          id: '',
          action: '',
          jobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss('payPeriodActionJobId')
      notification.error({
        title: 'Could not update pay period at this time',
        id: String(jobID)
      })
      setPayPeriodData({
        ...payPeriodData,
        jobId: ''
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        payPeriodActionJobId: ''
      })
    }
  })

  // Query to update pay period - returns jobID
  let selectedAction: PayPeriodActions
  const updateQuery = useMutation({
    mutationFn: ({ id, action }: { id: ID; action: PayPeriodActions }) => {
      selectedAction = action
      setPayPeriodData({
        ...payPeriodData,
        action,
        id
      })
      return APIService.payPeriods.BatchUpdatePayPeriodReports({
        id,
        action
      })
    },
    onSuccess: (data) => {
      notification.loading({
        title:
          selectedAction === 'approve'
            ? 'Approving in progress...'
            : 'Updating pay period...',
        subtitle:
          'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
        duration: Infinity,
        id: 'payPeriodActionJobId'
      })
      setTimeout(() => {
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodActionJobId: data.token_id
        })
      }, 0)
      setPayPeriodData({
        ...payPeriodData,
        jobId: data.token_id
      })
      onSuccess?.()
    },
    onError: () => {
      notification.error({
        title: 'An error has occurred trying to update your pay period',
        id: 'payPeriodAction'
      })
    }
  })

  return {
    updateQuery
  }
}

export default usePayPeriodAction
